import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { Suspense, useEffect, useMemo, useState } from 'react'

// Layouts & auth stay eager (first paint / login)
import AuthLayout from '@/layouts/AuthLayout'
import DashboardLayout from '@/layouts/DashboardLayout'
import PublicLayout from '@/layouts/PublicLayout'
import ShopStoreLayout from '@/layouts/ShopStoreLayout'
import SignupLayout from '@/layouts/SignupLayout'
import HelpLayout from '@/pages/help/HelpLayout'
import StaffLogin from '@/pages/auth/StaffLogin'
import CustomerLogin from '@/pages/auth/CustomerLogin'
import CustomerRegister from '@/pages/auth/CustomerRegister'
import CustomerVerifyEmail from '@/pages/auth/CustomerVerifyEmail'
import ForgotPassword from '@/pages/auth/ForgotPassword'
import ResetPassword from '@/pages/auth/ResetPassword'
import SetupTwoFactor from '@/pages/auth/SetupTwoFactor'
import CustomerPortalSectionGuard from '@/components/customer/CustomerPortalSectionGuard'
import { ShopStorefrontChildRoutes } from '@/routes/shopStorefrontRoutes'
import { useCustomDomainShop } from '@/hooks/useCustomDomainShop'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import ProtectedRoute from '@/components/ProtectedRoute'
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary'

import {
  Dashboard,
  ShopManagement,
  BranchManagement,
  CustomerManagement,
  ProductManagement,
  OrderManagement,
  QuotationManagement,
  OutboundShipmentManagement,
  OutboundShipmentDetail,
  PaymentManagement,
  PaymentIntentsManagement,
  StaffManagement,
  SupplierManagement,
  BrandManagement,
  PurchaseOrderManagement,
  SubscriptionManagement,
  RentalLeaseManagement,
  TagManagement,
  ReferrerManagement,
  DataManagement,
  PayrollManagement,
  StaffTimeEntryManagement,
  StaffLeaveManagement,
  ClassPackageManagement,
  JournalManagement,
  VendorBillManagement,
  OrderReturnManagement,
  BookingManagement,
  ScheduleManagement,
  BillUnlinkedSessionsPage,
  ScheduleResourcesPage,
  Reports,
  CustomerProductReconcileReport,
  SubscriptionReport,
  StaffSessionReport,
  PaymentReport,
  BookingReport,
  AgencyReferrerReport,
  OrderReport,
  InventoryReport,
  BusinessReportPage,
  Settings,
  StaffProfile,
  MessageTemplateManagement,
  MessageComposer,
  SupportInbox,
  AdminPlatformSupport,
  TeamManagement,
  CollaborationBoard,
  TeamFiles,
  EventManagement,
  EventFormManagement,
  CouponManagement,
  SocialPromote,
  CompanionBundleManagement,
  MembershipManagement,
  AutomationTasks,
  WebsiteDesigner,
  PageEditor,
  WebsiteBlogPage,
  WebsiteTemplatesPage,
  WebsiteAnalyticsPage,
  WebsiteReviewsPage,
  AdminPlatform,
  AdminPlatformRedirect,
  AdminPlatformSubscriptions,
  AdminPlatformNotifications,
  AdminPlatformNurture,
  HostBackups,
  PlatformReports,
  PlatformRemittanceReport,
  Unauthorized,
  CustomerDashboard,
  ProductCatalog,
  ShoppingCart,
  OrderHistory,
  CustomerOrderDetail,
  CustomerDownloads,
  CustomerTickets,
  CustomerRegisterPayment,
  CustomerPaymentReturn,
  MyCoupons,
  MyMembership,
  MySubscriptions,
  CustomerSubscriptionDetail,
  CustomerProfile,
  CustomerSchedule,
  CustomerSupport,
  HomePage,
  PricingPage,
  PartnersPage,
  OnboardingPage,
  ExploreDashboardPage,
  HelpHubPage,
  HelpContactPage,
  HelpCategoryPage,
  HelpArticlePage,
  TermsPage,
  PrivacyPage,
  DpaPage,
  ShopCatalog,
  PublicForm,
  PublicOrderPayPage,
  PublicOrderPayReturnPage,
  SignupRegisterPage,
  SignupCheckEmailPage,
  SignupVerifyPage,
  SignupContinuePage,
  SignupSetupPage,
} from '@/appLazyPages'

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <LoadingSpinner />
    </div>
  )
}

// Lazy pages suspend inside each layout's Outlet Suspense. Do not wrap <Routes>
// here — that unmounts header/sidebar and looks like a blank page until reload.

function App() {
  const { user, isAuthenticated } = useAuthStore()
  const [appEnv, setAppEnv] = useState<string>('')

  useEffect(() => {
    let cancelled = false
    fetch('/api/meta/environment', { credentials: 'same-origin' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`env endpoint failed: ${r.status}`)
        return r.json()
      })
      .then((payload) => {
        if (cancelled) return
        const env = String(payload?.data?.app_env || '').trim().toLowerCase()
        setAppEnv(env)
      })
      .catch(() => {
        if (cancelled) return
        // Fallback for local dev when backend endpoint is unavailable.
        const fallback = import.meta.env.MODE === 'development' ? 'dev' : ''
        setAppEnv(fallback)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const envBadge = useMemo(() => {
    if (!appEnv || appEnv === 'production' || appEnv === 'prod') return null
    if (appEnv === 'uat') {
      return { text: 'UAT', className: 'bg-amber-100 text-amber-900 border-amber-200' }
    }
    if (appEnv === 'staging') {
      return { text: 'STAGING', className: 'bg-purple-100 text-purple-900 border-purple-200' }
    }
    if (appEnv === 'dev' || appEnv === 'development') {
      return { text: 'DEV', className: 'bg-blue-100 text-blue-900 border-blue-200' }
    }
    return { text: appEnv.toUpperCase(), className: 'bg-gray-100 text-gray-900 border-gray-200' }
  }, [appEnv])

  const customDomainShop = useCustomDomainShop()

  if (customDomainShop === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  const routes = customDomainShop ? (
    <Routes>
      <Route path="/auth/*" element={<AuthLayout />}>
        <Route path="staff/login" element={<StaffLogin />} />
        <Route path="customer/login" element={<CustomerLogin />} />
        <Route path="customer/register" element={<CustomerRegister />} />
        <Route path="customer/verify-email" element={<CustomerVerifyEmail />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="setup-2fa" element={<SetupTwoFactor />} />
      </Route>
      <Route
        path="/*"
        element={
          <ShopStoreLayout slugOverride={customDomainShop.slug} basePathOverride="" />
        }
      >
        {ShopStorefrontChildRoutes()}
      </Route>
    </Routes>
  ) : (
    <Routes>
      {/* Tenant storefront: full layout is ShopStoreLayout only (no OMS PublicHeader/Footer) */}
      <Route path="/shop/:slug" element={<ShopStoreLayout />}>
        {ShopStorefrontChildRoutes()}
      </Route>

      {/* Public marketing / directory (OMS chrome) */}
      <Route path="/" element={<PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route path="pricing" element={<PricingPage />} />
        <Route path="partners" element={<PartnersPage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="demo/explore-dashboard" element={<ExploreDashboardPage />} />
        <Route path="shops" element={<ShopCatalog />} />
        <Route path="help" element={<HelpLayout />}>
          <Route index element={<HelpHubPage />} />
          <Route path="contact" element={<HelpContactPage />} />
          <Route path=":category" element={<HelpCategoryPage />} />
          <Route path=":category/:slug" element={<HelpArticlePage />} />
        </Route>
        <Route path="legal/terms" element={<TermsPage />} />
        <Route path="legal/privacy" element={<PrivacyPage />} />
        <Route path="legal/dpa" element={<DpaPage />} />
        <Route path="forms/:publicId" element={<PublicForm />} />
        <Route path="pay/:token" element={<PublicOrderPayPage />} />
        <Route path="pay/:token/return" element={<PublicOrderPayReturnPage />} />
      </Route>

      {/* ShopQuay self-serve signup funnel */}
      <Route path="/signup" element={<SignupLayout />}>
        <Route index element={<SignupRegisterPage />} />
        <Route path="check-email" element={<SignupCheckEmailPage />} />
        <Route path="verify" element={<SignupVerifyPage />} />
        <Route path="continue" element={<SignupContinuePage />} />
        <Route path="setup" element={<SignupSetupPage />} />
      </Route>

      {/* Auth Routes */}
      <Route path="/auth" element={<AuthLayout />}>
        <Route path="staff/login" element={<StaffLogin />} />
        <Route path="customer/login" element={<CustomerLogin />} />
        <Route path="customer/register" element={<CustomerRegister />} />
        <Route path="customer/verify-email" element={<CustomerVerifyEmail />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="setup-2fa" element={<SetupTwoFactor />} />
      </Route>

      {/* Staff/Admin Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requireAuth requireStaff>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="shops/new" element={<ShopManagement />} />
        <Route path="shops/:shopId/edit" element={<ShopManagement />} />
        <Route path="shops/:shopId" element={<ShopManagement />} />
        <Route path="shops" element={<ShopManagement />} />
        <Route path="branches/new" element={<BranchManagement />} />
        <Route path="branches/:branchId/edit" element={<BranchManagement />} />
        <Route path="branches/:branchId" element={<BranchManagement />} />
        <Route path="branches" element={<BranchManagement />} />
        <Route path="customers/new" element={<CustomerManagement />} />
        <Route path="customers/:customerId/edit" element={<CustomerManagement />} />
        <Route path="customers/:customerId" element={<CustomerManagement />} />
        <Route path="customers" element={<CustomerManagement />} />
        <Route path="products/new" element={<ProductManagement />} />
        <Route path="products/:productId/stats" element={<ProductManagement />} />
        <Route path="products/:productId/edit" element={<ProductManagement />} />
        <Route path="products/:productId" element={<ProductManagement />} />
        <Route path="products" element={<ProductManagement />} />
        <Route path="orders/new" element={<OrderManagement />} />
        <Route path="orders/stats" element={<OrderManagement />} />
        <Route path="orders/:orderId/edit" element={<OrderManagement />} />
        <Route path="orders/:orderId" element={<OrderManagement />} />
        <Route path="orders" element={<OrderManagement />} />
        <Route path="order-returns" element={<OrderReturnManagement />} />
        <Route path="quotations/new" element={<QuotationManagement />} />
        <Route path="quotations/:quotationId/edit" element={<QuotationManagement />} />
        <Route path="quotations/:quotationId" element={<QuotationManagement />} />
        <Route path="quotations" element={<QuotationManagement />} />
        <Route path="outbound-shipments" element={<OutboundShipmentManagement />} />
        <Route path="outbound-shipments/:outboundShipmentId" element={<OutboundShipmentDetail />} />
        <Route path="payments/intents/:intentId" element={<PaymentIntentsManagement />} />
        <Route path="payments/intents" element={<PaymentIntentsManagement />} />
        <Route path="payments/new" element={<PaymentManagement />} />
        <Route path="payments/stats" element={<PaymentManagement />} />
        <Route path="payments/:paymentId/edit" element={<PaymentManagement />} />
        <Route path="payments/:paymentId" element={<PaymentManagement />} />
        <Route path="payments" element={<PaymentManagement />} />
        <Route path="staff/new" element={<StaffManagement />} />
        <Route path="staff/:staffId/edit" element={<StaffManagement />} />
        <Route path="staff/:staffId" element={<StaffManagement />} />
        <Route path="staff" element={<StaffManagement />} />
        <Route path="brands/new" element={<BrandManagement />} />
        <Route path="brands/:brandId/edit" element={<BrandManagement />} />
        <Route path="brands" element={<BrandManagement />} />
        <Route path="suppliers/new" element={<SupplierManagement />} />
        <Route path="suppliers/:supplierId/edit" element={<SupplierManagement />} />
        <Route path="suppliers/:supplierId/addresses/new" element={<SupplierManagement />} />
        <Route path="suppliers/:supplierId/addresses/:addressId/edit" element={<SupplierManagement />} />
        <Route path="suppliers/:supplierId" element={<SupplierManagement />} />
        <Route path="suppliers" element={<SupplierManagement />} />
        <Route path="purchase-orders/new" element={<PurchaseOrderManagement />} />
        <Route path="purchase-orders/:purchaseOrderId/edit" element={<PurchaseOrderManagement />} />
        <Route path="purchase-orders/:purchaseOrderId" element={<PurchaseOrderManagement />} />
        <Route path="purchase-orders" element={<PurchaseOrderManagement />} />
        <Route path="subscription/:subscriptionId" element={<SubscriptionManagement />} />
        <Route path="subscription" element={<SubscriptionManagement />} />
        <Route path="leases/:subscriptionId" element={<RentalLeaseManagement />} />
        <Route path="leases" element={<RentalLeaseManagement />} />
        <Route path="tags/new" element={<TagManagement />} />
        <Route path="tags/:tagId/edit" element={<TagManagement />} />
        <Route path="tags/:tagId" element={<TagManagement />} />
        <Route path="tags" element={<TagManagement />} />
        <Route path="referrers/new" element={<ReferrerManagement />} />
        <Route path="referrers/:referrerId/edit" element={<ReferrerManagement />} />
        <Route path="referrers/:referrerId" element={<ReferrerManagement />} />
        <Route path="referrers" element={<ReferrerManagement />} />
        <Route path="website" element={<WebsiteDesigner />} />
        <Route path="website/blog" element={<WebsiteBlogPage />} />
        <Route path="website/templates" element={<WebsiteTemplatesPage />} />
        <Route path="website/analytics" element={<WebsiteAnalyticsPage />} />
        <Route path="website/reviews" element={<WebsiteReviewsPage />} />
        <Route path="website/pages/:pageId" element={<PageEditor />} />
        <Route path="coupons/templates/new" element={<CouponManagement />} />
        <Route path="coupons/templates/:templateId/edit" element={<CouponManagement />} />
        <Route path="coupons/templates/:templateId" element={<CouponManagement />} />
        <Route path="coupons/rules/new" element={<CouponManagement />} />
        <Route path="coupons/rules/:ruleId/edit" element={<CouponManagement />} />
        <Route path="coupons" element={<CouponManagement />} />
        <Route path="social-promote" element={<SocialPromote />} />
        <Route path="companion-bundles/new" element={<CompanionBundleManagement />} />
        <Route path="companion-bundles/:ruleId/edit" element={<CompanionBundleManagement />} />
        <Route path="companion-bundles" element={<CompanionBundleManagement />} />
        <Route path="membership" element={<MembershipManagement />} />
        <Route path="teams/new" element={<TeamManagement />} />
        <Route path="teams/:teamId/members" element={<TeamManagement />} />
        <Route path="teams" element={<TeamManagement />} />
        <Route path="collaboration" element={<CollaborationBoard />} />
        <Route path="team-files" element={<TeamFiles />} />
        <Route path="messaging/templates" element={<MessageTemplateManagement />} />
        <Route path="messaging/send" element={<MessageComposer />} />
        <Route path="support" element={<SupportInbox />} />
        <Route path="platform-support" element={<Navigate to="/help/contact" replace />} />
        <Route path="payroll" element={<PayrollManagement />} />
        <Route path="staff-time-entries" element={<StaffTimeEntryManagement />} />
        <Route path="staff-leave" element={<StaffLeaveManagement />} />
        <Route path="class-packages/:packageId" element={<ClassPackageManagement />} />
        <Route path="class-packages" element={<ClassPackageManagement />} />
        <Route path="automation-tasks/:taskId" element={<AutomationTasks />} />
        <Route path="automation-tasks" element={<AutomationTasks />} />
        <Route path="journal/entries/new" element={<JournalManagement />} />
        <Route path="journal/entries/:entryId" element={<JournalManagement />} />
        <Route path="journal/accounts/new" element={<JournalManagement />} />
        <Route path="journal/reports/trial-balance" element={<JournalManagement />} />
        <Route path="journal/reports/financial-statements" element={<JournalManagement />} />
        <Route path="journal/reports/general-ledger" element={<JournalManagement />} />
        <Route path="journal/reports/stock-schedule" element={<JournalManagement />} />
        <Route path="journal/reports/journal-register" element={<JournalManagement />} />
        <Route path="journal/reports/accountant-pack" element={<JournalManagement />} />
        <Route path="journal/reports/month-end-close" element={<JournalManagement />} />
        <Route path="journal/reports/xero-export" element={<JournalManagement />} />
        <Route path="journal/bank-reconciliation" element={<JournalManagement />} />
        <Route path="journal" element={<JournalManagement />} />
        <Route path="vendor-bills/new" element={<VendorBillManagement />} />
        <Route path="vendor-bills/:billId/edit" element={<VendorBillManagement />} />
        <Route path="vendor-bills/:billId/pay" element={<VendorBillManagement />} />
        <Route path="vendor-bills/:billId" element={<VendorBillManagement />} />
        <Route path="vendor-bills" element={<VendorBillManagement />} />
        <Route path="bookings/new" element={<BookingManagement />} />
        <Route path="bookings/:bookingId/edit" element={<BookingManagement />} />
        <Route path="bookings/:bookingId" element={<BookingManagement />} />
        <Route path="bookings" element={<BookingManagement />} />
        <Route path="schedule/resources" element={<ScheduleResourcesPage />} />
        <Route path="schedule/unlinked-billing" element={<BillUnlinkedSessionsPage />} />
        <Route path="schedule/new" element={<ScheduleManagement />} />
        <Route path="schedule/:sessionId/edit" element={<ScheduleManagement />} />
        <Route path="schedule/:sessionId" element={<ScheduleManagement />} />
        <Route path="schedule" element={<ScheduleManagement />} />
        <Route path="events/forms/new" element={<EventFormManagement />} />
        <Route path="events/forms/:formId/submissions" element={<EventFormManagement />} />
        <Route path="events/forms/:formId/edit" element={<EventFormManagement />} />
        <Route path="events/forms" element={<EventFormManagement />} />
        <Route path="events/new" element={<EventManagement />} />
        <Route path="events/:eventId/tickets" element={<EventManagement />} />
        <Route path="events/:eventId/edit" element={<EventManagement />} />
        <Route path="events" element={<EventManagement />} />
        <Route path="data-management" element={<DataManagement />} />
        <Route path="reports/customer-product-reconcile" element={<CustomerProductReconcileReport />} />
        <Route path="reports/subscriptions" element={<SubscriptionReport />} />
        <Route path="reports/staff-sessions" element={<StaffSessionReport />} />
        <Route path="reports/payments" element={<PaymentReport />} />
        <Route path="reports/bookings" element={<BookingReport />} />
        <Route path="reports/agency-referrers" element={<AgencyReferrerReport />} />
        <Route path="reports/orders" element={<OrderReport />} />
        <Route path="reports/inventory" element={<InventoryReport />} />
        <Route path="reports/business/:reportKey" element={<BusinessReportPage />} />
        <Route path="reports" element={<Reports />} />
        <Route path="profile" element={<StaffProfile />} />
        <Route path="settings/:sectionKey?" element={<Settings />} />
        <Route
          path="admin/support"
          element={
            <ProtectedRoute requireAuth requireStaff>
              <AdminPlatformSupport />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin"
          element={
            <ProtectedRoute requireAuth requireStaff requireRole={['super_admin']}>
              <AdminPlatform />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/quota"
          element={
            <ProtectedRoute requireAuth requireStaff requireRole={['super_admin']}>
              <AdminPlatformRedirect tab="quota" />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/subscriptions"
          element={
            <ProtectedRoute requireAuth requireStaff requireRole={['super_admin']}>
              <AdminPlatformSubscriptions />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/security"
          element={
            <ProtectedRoute requireAuth requireStaff requireRole={['super_admin']}>
              <AdminPlatformRedirect tab="security" />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/payment-gateways"
          element={
            <ProtectedRoute requireAuth requireStaff requireRole={['super_admin']}>
              <AdminPlatformRedirect tab="payment-gateways" />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/host-backups"
          element={
            <ProtectedRoute requireAuth requireStaff requireRole={['super_admin']}>
              <HostBackups />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/reports"
          element={
            <ProtectedRoute requireAuth requireStaff requireRole={['super_admin']}>
              <PlatformReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/reports/remittance"
          element={
            <ProtectedRoute requireAuth requireStaff requireRole={['super_admin']}>
              <PlatformRemittanceReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/notifications"
          element={
            <ProtectedRoute requireAuth requireStaff requireRole={['super_admin']}>
              <AdminPlatformNotifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/nurture"
          element={
            <ProtectedRoute requireAuth requireStaff requireRole={['super_admin']}>
              <AdminPlatformNurture />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route
        path="/unauthorized"
        element={
          <Suspense fallback={<RouteFallback />}>
            <Unauthorized />
          </Suspense>
        }
      />

      {/* Customer Dashboard Routes */}
      <Route
        path="/customer"
        element={
          <ProtectedRoute requireAuth requireCustomer>
            <DashboardLayout isCustomer />
          </ProtectedRoute>
        }
      >
        <Route index element={<CustomerDashboard />} />
        <Route
          path="products"
          element={
            <CustomerPortalSectionGuard section="products">
              <ProductCatalog />
            </CustomerPortalSectionGuard>
          }
        />
        <Route
          path="cart"
          element={
            <CustomerPortalSectionGuard section="cart">
              <ShoppingCart />
            </CustomerPortalSectionGuard>
          }
        />
        <Route
          path="orders"
          element={
            <CustomerPortalSectionGuard section="orders">
              <OrderHistory />
            </CustomerPortalSectionGuard>
          }
        />
        <Route
          path="downloads"
          element={
            <CustomerPortalSectionGuard section="downloads">
              <CustomerDownloads />
            </CustomerPortalSectionGuard>
          }
        />
        <Route
          path="tickets"
          element={
            <CustomerPortalSectionGuard section="tickets">
              <CustomerTickets />
            </CustomerPortalSectionGuard>
          }
        />
        <Route
          path="orders/:orderId/register-payment"
          element={
            <CustomerPortalSectionGuard section="orders">
              <CustomerRegisterPayment />
            </CustomerPortalSectionGuard>
          }
        />
        <Route
          path="orders/:orderId/payment-return"
          element={
            <CustomerPortalSectionGuard section="orders">
              <CustomerPaymentReturn />
            </CustomerPortalSectionGuard>
          }
        />
        <Route
          path="orders/:orderId"
          element={
            <CustomerPortalSectionGuard section="orders">
              <CustomerOrderDetail />
            </CustomerPortalSectionGuard>
          }
        />
        <Route
          path="coupons"
          element={
            <CustomerPortalSectionGuard section="coupons">
              <MyCoupons />
            </CustomerPortalSectionGuard>
          }
        />
        <Route
          path="membership"
          element={
            <CustomerPortalSectionGuard section="membership">
              <MyMembership />
            </CustomerPortalSectionGuard>
          }
        />
        <Route
          path="subscriptions"
          element={
            <CustomerPortalSectionGuard section="subscriptions">
              <MySubscriptions />
            </CustomerPortalSectionGuard>
          }
        />
        <Route
          path="subscriptions/:subscriptionId"
          element={
            <CustomerPortalSectionGuard section="subscriptions">
              <CustomerSubscriptionDetail />
            </CustomerPortalSectionGuard>
          }
        />
        <Route
          path="schedule"
          element={
            <CustomerPortalSectionGuard section="schedule">
              <CustomerSchedule />
            </CustomerPortalSectionGuard>
          }
        />
        <Route path="support" element={<CustomerSupport />} />
        <Route path="profile" element={<CustomerProfile />} />
      </Route>

      {/* Redirect authenticated users */}
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to={user?.is_web_user ? '/customer' : '/dashboard'} replace />
          ) : (
            <Navigate to="/auth/staff/login" replace />
          )
        }
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )

  return (
    <>
      {envBadge && (
        <div className="fixed bottom-3 left-3 z-40 pointer-events-none">
          <div
            className={`px-2.5 py-1 rounded-full text-xs font-semibold border shadow-sm ${envBadge.className}`}
          >
            {envBadge.text}
          </div>
        </div>
      )}
      <RouteErrorBoundary>{routes}</RouteErrorBoundary>
    </>
  )
}

export default App
